<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  config: { type: Object, required: true },
  contactCount: { type: Number, default: 0 },
});

const { t } = useI18n();

const estimatedSeconds = computed(() => {
  const {
    burst_size: bs,
    burst_delay_min_ms: bdMin,
    burst_delay_max_ms: bdMax,
    cooldown_min_seconds: cdMin,
    cooldown_max_seconds: cdMax,
  } = props.config;
  if (!bs || !bdMin || !bdMax || !cdMin || !cdMax || props.contactCount <= 0)
    return 0;

  const avgDelaySec = (bdMin + bdMax) / 2000;
  const burstDuration = bs * avgDelaySec;
  const avgCooldown = (cdMin + cdMax) / 2;
  const totalBursts = Math.ceil(props.contactCount / bs);

  // Last burst doesn't need cooldown after it
  const lastBurstContacts = props.contactCount % bs || bs;
  const lastBurstTime = (lastBurstContacts - 1) * avgDelaySec;
  const fullBurstsTime =
    Math.max(0, totalBursts - 1) * (burstDuration + avgCooldown);

  return fullBurstsTime + lastBurstTime;
});

const formattedTime = computed(() => {
  const totalSec = estimatedSeconds.value;
  if (totalSec <= 0) return '-';

  const hours = Math.floor(totalSec / 3600);
  const minutes = Math.ceil((totalSec % 3600) / 60);

  if (hours > 0) return `~${hours}h ${minutes}min`;
  return `~${minutes} min`;
});

const msgsPerMinute = computed(() => {
  const {
    burst_size: bs,
    burst_delay_min_ms: bdMin,
    burst_delay_max_ms: bdMax,
    cooldown_min_seconds: cdMin,
    cooldown_max_seconds: cdMax,
  } = props.config;
  if (!bs || !bdMin || !bdMax || !cdMin || !cdMax) return 0;

  const avgDelaySec = (bdMin + bdMax) / 2000;
  const burstDuration = bs * avgDelaySec;
  const avgCooldown = (cdMin + cdMax) / 2;
  const cycleTime = burstDuration + avgCooldown;

  return Math.round((bs / cycleTime) * 60);
});
</script>

<template>
  <div>
    <div
      v-if="contactCount > 0"
      class="flex items-center gap-2 p-3 rounded-lg bg-n-alpha-2"
    >
      <span class="i-lucide-clock size-4 text-n-slate-10 shrink-0" />
      <span class="text-sm text-n-slate-11">
        {{ t('CAMPAIGN.WHATSAPP.WIZARD.DISPATCH_SPEED.ESTIMATED_TIME') }}
      </span>
      <span class="text-sm font-medium text-n-slate-12">
        {{ formattedTime }}
      </span>
      <span class="text-xs text-n-slate-10">
        {{
          t('CAMPAIGN.WHATSAPP.WIZARD.DISPATCH_SPEED.RATE_SUMMARY', {
            contacts: t(
              'CAMPAIGN.WHATSAPP.WIZARD.DISPATCH_SPEED.ESTIMATED_CONTACTS',
              { count: contactCount }
            ),
            rate: msgsPerMinute,
          })
        }}
      </span>
    </div>
  </div>
</template>
